export const USER_ACCOUNT_STATE_FILTERS = [
  { id: "locked", label: "Locked accounts" },
  { id: "no_tenant", label: "No tenant" },
  { id: "never_logged_in", label: "Never logged in" },
];

export const ACCOUNT_ISSUE_LABELS = {
  locked: "Locked",
  no_tenant: "No tenant",
  never_logged_in: "Never logged in",
  tenant_suspended: "Tenant suspended",
};

export const ACCOUNT_ISSUE_VARIANTS = {
  locked: "danger",
  no_tenant: "warning",
  never_logged_in: "neutral",
  tenant_suspended: "danger",
};

export const analyzeUserAccount = (user) => {
  const support = user?.support ?? {};
  const affiliations = user?.affiliations ?? [];

  const isLocked = support.is_locked ?? user?.status === false;
  const hasAffiliation = support.has_affiliation ?? affiliations.length > 0;
  const neverLoggedIn = support.never_logged_in ?? !user?.last_login_at;
  const hasSuspendedTenant =
    support.has_suspended_tenant ??
    affiliations.some((item) => item.tenant_status === "suspended");

  const issues =
    support.issues ??
    [
      isLocked ? "locked" : null,
      !hasAffiliation ? "no_tenant" : null,
      neverLoggedIn ? "never_logged_in" : null,
      hasSuspendedTenant ? "tenant_suspended" : null,
    ].filter(Boolean);

  return {
    isLocked,
    hasAffiliation,
    neverLoggedIn,
    hasSuspendedTenant,
    issues,
  };
};

export const canRestoreLoginAccess = (permissions = {}) =>
  Boolean(permissions.lock && permissions.revoke && permissions.mfa);

export const getSelfServiceEmailGuidance = (user) => {
  switch (user?.user_type) {
    case "sp_standard_user":
    case "sp_cos":
      return {
        title: "Worker self-service",
        steps: [
          "Sign in to the worker portal.",
          "Open Settings → Profile.",
          "Update login email there (updates Cognito and profile).",
        ],
      };
    case "sp_admin":
      return {
        title: "Organisation owner self-service",
        steps: [
          "Sign in as the SP Admin.",
          "Open Organisation / account settings.",
          "Update organisation contact email and profile details.",
        ],
      };
    case "sp_manager":
    case "hr_officer":
      return {
        title: "Manager self-service",
        steps: [
          "Sign in to the manager portal.",
          "Update profile details from account settings where available.",
          "For login email changes, an SP Admin may need to update the user record.",
        ],
      };
    default:
      return {
        title: "Self-service",
        steps: [
          "Ask the user to sign in and update profile details from account settings.",
          "If they cannot sign in, resolve login access first.",
        ],
      };
  }
};

export const getUserSupportPlaybooks = (user, permissions = {}) => {
  if (!user) {
    return [];
  }

  const account = analyzeUserAccount(user);
  const playbooks = [];

  if (
    account.isLocked ||
    account.neverLoggedIn ||
    account.hasSuspendedTenant ||
    !account.hasAffiliation
  ) {
    playbooks.push({
      id: "cant_sign_in",
      title: "Can't sign in",
      description:
        "Typical login failures: locked account, stale session, or MFA mismatch.",
      steps: [
        account.isLocked ? "Unlock the account in Cognito." : null,
        "Revoke all active sessions.",
        "Reset MFA so the user can re-enrol on next login.",
        permissions.passwordReset
          ? "Set a temporary password if the user forgot it."
          : null,
      ].filter(Boolean),
      primaryAction: canRestoreLoginAccess(permissions)
        ? { id: "restore_login", label: "Restore login access" }
        : null,
    });
  }

  if (permissions.passwordReset) {
    playbooks.push({
      id: "forgot_password",
      title: "Forgot password",
      description: "Set a new password the user can use immediately.",
      steps: ["Set a temporary password and share it through your secure channel."],
      primaryAction: { id: "reset_password", label: "Set new password" },
    });
  }

  const emailGuidance = getSelfServiceEmailGuidance(user);
  playbooks.push({
    id: "change_email",
    title: "Change login email",
    description:
      "Users can update their own email when signed in. Use admin update only if they cannot access the portal.",
    steps: [
      ...emailGuidance.steps,
      permissions.emailUpdate
        ? "Or update the login email from here (updates Cognito + user record)."
        : null,
    ].filter(Boolean),
    primaryAction: permissions.emailUpdate
      ? { id: "change_email", label: "Update email (admin)" }
      : null,
    guidanceTitle: emailGuidance.title,
  });

  playbooks.push({
    id: "update_profile",
    title: "Update name / profile details",
    description: "Most profile fields are edited by the user inside the tenant portal.",
    steps: [
      "Ask the user to sign in and update their profile from account settings.",
      user?.user_type === "sp_standard_user" || user?.user_type === "sp_cos"
        ? "Workers: Settings → Profile."
        : "Managers and admins: organisation or user settings.",
      permissions.ghost
        ? "Ghost as the user to verify where settings live for their role."
        : null,
    ].filter(Boolean),
    primaryAction: permissions.ghost ? { id: "ghost", label: "Ghost as user" } : null,
  });

  if (permissions.mfa) {
    playbooks.push({
      id: "lost_mfa",
      title: "Lost MFA device",
      description: "Clear MFA and force a fresh enrolment.",
      steps: [
        "Reset MFA in Cognito.",
        permissions.revoke ? "Revoke sessions so old tokens cannot be reused." : null,
      ].filter(Boolean),
      primaryAction: { id: "mfa", label: "Reset MFA" },
    });
  }

  if (permissions.revoke) {
    playbooks.push({
      id: "session_stuck",
      title: "Session stuck / forced logout",
      description: "User sees an old session or cannot refresh their token.",
      steps: ["Revoke all Cognito sessions globally."],
      primaryAction: { id: "revoke", label: "Revoke sessions" },
    });
  }

  if (!account.hasAffiliation) {
    playbooks.push({
      id: "no_tenant_access",
      title: "No tenant access",
      description:
        "User exists but is not linked to a service provider. Assign them from the tenant detail page.",
      steps: [
        "Open the tenant and add the user as admin, manager, or worker.",
        "Confirm the user's email matches the record they are trying to use.",
      ],
      primaryAction: null,
    });
  }

  if (account.hasSuspendedTenant) {
    playbooks.push({
      id: "tenant_suspended",
      title: "Tenant suspended",
      description: "Login may succeed but tenant features are blocked.",
      steps: [
        "Review the tenant status on the tenant detail page.",
        "Reactivate the tenant or move the user to an active tenant if appropriate.",
      ],
      primaryAction: null,
      tenantLink: user.affiliations?.find((item) => item.tenant_status === "suspended")
        ?.sp_id,
    });
  }

  if (permissions.ghost) {
    playbooks.push({
      id: "verify_experience",
      title: "Verify as user",
      description: "Ghost into the portal to reproduce what the user sees.",
      steps: ["Start a ghost session under the user's tenant context."],
      primaryAction: { id: "ghost", label: "Ghost as user" },
    });
  }

  const primaryAffiliation = user.affiliations?.[0];
  const rosterUserTypes = new Set([
    "sp_standard_user",
    "sp_cos",
    "sp_manager",
    "hr_officer",
  ]);

  if (
    primaryAffiliation?.sp_id &&
    rosterUserTypes.has(user?.user_type)
  ) {
    playbooks.push({
      id: "roster_access_issue",
      title: "Roster or shift access issue",
      description:
        "Use tenant operational guides for roster visibility, shift acceptance, and availability.",
      steps: [
        "Open the tenant support drawer for operational playbooks.",
        "Use Open in tenant to jump to rostering or roles settings.",
        permissions.ghost
          ? "Ghost as the user if you need to reproduce their view."
          : null,
      ].filter(Boolean),
      tenantSupportLink: `/tenants/${primaryAffiliation.sp_id}?support=1`,
    });
  }

  if (user?.user_type === "sp_admin" && primaryAffiliation?.sp_id) {
    playbooks.push({
      id: "subscription_help",
      title: "Subscription or plan change",
      description: "Guide the SP Admin through subscription self-service or billing review.",
      steps: [
        "Open tenant support guides for the subscription playbook.",
        "If the tenant is on wildcard billing, review the Billing tab instead of tenant checkout.",
      ],
      tenantSupportLink: `/tenants/${primaryAffiliation.sp_id}?support=1`,
    });
  }

  return playbooks;
};

export const buildRestoreLoginSteps = (user) => {
  const account = analyzeUserAccount(user);

  return [
    account.isLocked ? "Unlock account" : null,
    "Revoke all sessions",
    "Reset MFA",
  ].filter(Boolean);
};
